import type { Messages } from "./en";

const it: Messages = {
  meta: {
    siteTagline: "Elettronica del futuro per la circolarità, con materiali a base biologica",
    homeDescription:
      "Una raccolta curata di materiali a base biologica e biodegradabili per l'elettronica, ciascuno collegato alla propria fonte, insieme ai progetti che li utilizzano.",
    materialsTitle: "Database dei materiali",
    materialsDescription:
      "Esplora materiali a base biologica e biodegradabili per l'elettronica. Filtra per famiglia, dati di degradazione, resistenza a trazione e fonte. Ogni scheda rimanda alla pubblicazione di origine.",
    applicationsTitle: "Applicazioni",
    applicationsDescription:
      "Progetti e prototipi che impiegano materiali a base biologica e biodegradabili nell'elettronica, ciascuno con la propria fonte.",
    aboutTitle: "Chi siamo",
    aboutDescription: "Cos'è BioNexus, come vengono selezionate e documentate le schede e come contribuire.",
    materialDescription: (name, family) =>
      `${name}${family ? ` (${family})` : ""}: proprietà misurate e pubblicazione di origine su BioNexus.`,
    applicationDescription: (title) => `${title}: dettagli del progetto, materiali utilizzati e fonte su BioNexus.`,
  },
  nav: {
    materials: "Materiali",
    applications: "Applicazioni",
    about: "Chi siamo",
    admin: "Admin",
    light: "Chiaro",
    dark: "Scuro",
    auto: "Auto",
    signOut: "Esci",
    language: "Lingua",
  },
  common: {
    backHome: "Torna alla home",
    backMaterials: "Torna ai materiali",
    backApplications: "Torna alle applicazioni",
    viewAll: "Vedi tutto",
    readCase: "Leggi il caso",
    lastUpdated: (date) => `Ultimo aggiornamento: ${date}`,
    image: "Immagine",
    source: "Fonte",
    method: "Metodo",
    general: "Generale",
    onThisPage: "In questa pagina",
    notFound: "Pagina non trovata",
    returnHome: "Torna alla home",
    contentLanguageNote: "Contenuti della scheda nella lingua della fonte (inglese)",
  },
  applicationTypes: {
    academic: "Accademico",
    commercial: "Commerciale",
    "open-source": "Open source",
    community: "Comunità",
  },
  home: {
    heroA: "Elettronica del futuro per la",
    heroB: "circolarità,",
    heroC: "con materiali a base biologica",
    heroLead: "Una raccolta curata di materiali a base biologica per l'elettronica.",
    exploreMaterials: "Esplora i materiali",
    seeApplications: "Vedi le applicazioni",
    statMaterials: "Materiali",
    statApplications: "Applicazioni",
    statSources: "Fonti citate",
    latestApplications: "Ultime applicazioni",
    featuredMaterials: "Materiali in evidenza",
    noApplications: "Nessuna applicazione disponibile.",
    noMaterials: "Nessun materiale disponibile.",
  },
  loop: {
    stages: ["Crescere", "Produrre", "Usare", "Restituire"],
    aria: "Ciclo di vita del materiale: crescere, produrre, usare, restituire",
    footnote: "Il fine vita varia da materiale a materiale: consulta ogni fonte.",
  },
  materials: {
    eyebrow: "Database dei materiali",
    title: "Materiali a base biologica per l'elettronica",
    lead: "Ogni scheda rimanda alla propria fonte. I valori compaiono solo se riportati dalla fonte.",
    allFamilies: "Tutte le famiglie",
    filterLabel: "Filtra i materiali per completezza dei dati",
    filters: {
      all: "Tutti i dati",
      complete: "≥3 proprietà misurate",
      sourced: "Con articolo di riferimento",
      degradation: "Con dati di degradazione",
      tensile: "Con resistenza a trazione",
    },
    count: (n) => `${n} ${n === 1 ? "materiale" : "materiali"}`,
    empty: "Nessun materiale corrisponde ai filtri.",
    clearFilters: "Azzera i filtri",
    measured: (n) => `${n} ${n === 1 ? "proprietà misurata" : "proprietà misurate"}`,
    measuredTitle: "Tre o più proprietà misurate, ciascuna con fonte citata",
  },
  applications: {
    featured: "In evidenza",
    readTheCase: "Leggi il caso",
    all: "Tutte le applicazioni",
    count: (n) => `${n} ${n === 1 ? "caso" : "casi"}`,
    emptyTitle: "Nessuna applicazione",
    emptyText: "Torna a trovarci presto.",
  },
  material: {
    notFound: "Materiale non trovato",
    sections: {
      overview: "Panoramica",
      properties: "Proprietà misurate",
      abstract: "Abstract",
      fabrication: "Come si produce",
      context: "Contesto di ricerca",
      applications: "Impiegato in",
    },
    facts: { family: "Famiglia", primaryUse: "Uso principale", degradation: "Biodegradazione", trl: "TRL · valutazione BioNexus" },
    props: {
      degradation: "Biodegradazione",
      tensile: "Resistenza a trazione",
      electrical: "Prestazioni elettriche",
      water: "Resistenza all'acqua",
      cost: "Costo",
      trl: "Maturità tecnologica",
    },
    readFullAbstract: "Leggi l'abstract completo",
    showLess: "Mostra meno",
    comparedWith: "Confrontato con",
    referencedFigures: "Figure citate",
    researchSource: "Fonte di ricerca",
    viewPaper: "Vedi l'articolo",
    applicationsFromPaper: "Applicazioni (dall'articolo)",
    moreFromFamily: (family) => `Altri materiali della famiglia ${family}`,
    application: "Applicazione",
  },
  application: {
    notFound: "Applicazione non trovata",
    noDescription: "Nessuna descrizione disponibile.",
    sections: {
      overview: "Panoramica",
      features: "Caratteristiche principali",
      technology: "Come funziona",
      impact: "Impatto sulla sostenibilità",
      team: "Chi c'è dietro",
      materials: "Materiali utilizzati",
    },
    facts: { type: "Tipo", category: "Categoria", year: "Anno", trl: "TRL · valutazione BioNexus" },
    technologies: "Tecnologie",
    leadOrganization: "Ente capofila",
    projectSource: "Fonte del progetto",
    tags: "Tag",
    more: "Altre applicazioni",
  },
  trl: {
    stages: [
      "Principi di base osservati",
      "Concetto tecnologico formulato",
      "Prova sperimentale del concetto",
      "Tecnologia convalidata in laboratorio",
      "Tecnologia convalidata in ambiente rilevante (ambiente industrialmente rilevante nel caso delle tecnologie abilitanti fondamentali)",
      "Tecnologia dimostrata in ambiente rilevante (ambiente industrialmente rilevante nel caso delle tecnologie abilitanti fondamentali)",
      "Dimostrazione di un prototipo di sistema in ambiente operativo",
      "Sistema completo e qualificato",
      "Sistema reale provato in ambiente operativo (produzione competitiva nel caso delle tecnologie abilitanti fondamentali; o nello spazio)",
    ],
    assessedBy: "Valutato da BioNexus secondo la",
    scale: "scala TRL dell'UE",
    translationNote: " (traduzione non ufficiale)",
    dotsTitle: "TRL valutato da BioNexus",
    aria: (level, stage) => `Livello di maturità tecnologica ${level} su 9: ${stage} (valutato da BioNexus)`,
  },
  footer: {
    tagline: "Materiali a base biologica per l'elettronica",
    navigation: "Navigazione",
    contact: "Contatti",
    legal: "Note legali",
    rights: "Tutti i diritti riservati.",
    research: "Contenuti a scopo di ricerca.",
    colophon: "Colophon e riferimenti",
    data: "Dati: i valori misurati provengono dalla fonte citata in ciascuna scheda; i valori non riportati dalla fonte vengono omessi.",
    trlBefore: "La maturità tecnologica (TRL) è valutata da BioNexus secondo la",
    trlLink: "scala TRL di Horizon 2020 dell'UE (Allegati generali, parte G)",
    images:
      "Le immagini provengono dalle pubblicazioni e dalle pagine di progetto originali e sono accreditate e collegate in ogni scheda. Non vengono usate immagini generate con l'IA.",
    typefaces: "Caratteri:",
    typefacesAfter: "entrambi con licenza SIL Open Font License, distribuiti tramite Google Fonts.",
    icons: "Icone:",
    iconsAfter: "(licenza ISC). Realizzato con React, Tailwind CSS, Radix UI e shadcn/ui.",
    contrastBefore: "I colori di testo e interfaccia sono scelti per rispettare il",
    contrastLink: "contrasto WCAG 2.1 AA",
    contrastAfter: "; le animazioni si riducono quando il sistema lo richiede.",
    language:
      "Interfaccia disponibile in inglese, cinese e italiano. I contenuti delle schede (descrizioni, abstract, valori misurati) sono mostrati nella lingua della fonte, di solito l'inglese.",
  },
  about: {
    title: "Chi è BioNexus",
    whatTitle: "Cos'è BioNexus?",
    whatText:
      "Una piattaforma editoriale indipendente che documenta i materiali biodegradabili e le loro applicazioni IoT. Seguiamo la ricerca sottoposta a revisione paritaria, i progetti reali e gli sviluppi di mercato nell'elettronica rigenerativa.",
    whyTitle: "Perché è importante",
    why1a: "53,6 milioni di tonnellate di rifiuti elettronici prodotti nel 2019",
    why1b: ". Con miliardi di sensori IoT e dispositivi elettronici usa e getta che entrano negli ecosistemi, la degradabilità diventa essenziale.",
    why2a: "Mercato dei polimeri per elettronica biodegradabile: 126 M$ (2024) → 246 M$ (2033)",
    why2b: ". Dagli impianti medici all'agricoltura intelligente, i materiali che prima funzionano e poi si dissolvono stanno passando dalla ricerca alla realtà.",
    source1: "UN Global E-waste Monitor 2020",
    source1After: ", ITU/UNITAR/ISWA",
    source2: "Grand View Research",
    source2After: ", Biodegradable Electronics Polymers Market, ottobre 2024",
    approachTitle: "Il nostro approccio",
    approachText: "Fonti affidabili e selezione manuale per la ricerca sull'elettronica biodegradabile.",
    approachList: [
      "Priorità alle pubblicazioni sottoposte a revisione paritaria",
      "Revisione e verifica manuali",
      "Fonte citata per ogni dato",
      "Focus sulle applicazioni dell'elettronica biodegradabile",
    ],
    approachNote: "Non è un database esaustivo, ma una selezione curata di materiali e progetti pertinenti.",
    resourcesTitle: "Risorse correlate",
    databases: "Database di materiali",
    fmb: "Piattaforma condivisa di materiali e opere",
    materiom: "Ricette open source di biomateriali",
    contributeTitle: "Contribuisci",
    contributeText: "Hai trovato un articolo pertinente? Stai sviluppando un dispositivo biodegradabile?",
    submit: "Invia una pubblicazione",
  },
};

export default it;
